'use memo'

import { useEffect, useState } from 'react'
import { AgxRow } from '@urbanx/agx.ui-components2'
import { NswContractStage, QldContractStage } from '@urbanx/agx-ui-components'
import {
  CampaignDetailModelState,
  CoolingOffDetails,
  FormCondition,
  Condition,
  LinkedDateImpact,
  LinkedDateImpactSourceDateType,
  StaffPreviewLinkedDatesRequest,
  StaffPreviewLinkedDatesRequestSourceDateType,
  StaffUpdateContractDatesRequest,
  useInvalidateUseStaffGetContractDates,
  useStaffPreviewLinkedDates,
  useStaffGetContractDates,
  useStaffUpdateContractDates,
  WaiverOfCoolingOff,
} from '@urbanx/agx.campaigns.client'
import { MenuTitles, Pages } from 'constants/menu'
import { ErrorModal } from 'helpers/ErrorModal'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { isDirtyEqual } from 'utils/compares'
import { ContractSubPage } from '../ContractSubPage'
import { getContractInformationSubPages } from './ContractInformationSubPages'
import { ConditionPanel } from './panels/ConditionPanel'
import { AddCoolingOffPeriod } from './modals/AddCoolingOffPeriod'
import { CoolingOffPeriodPanel } from './panels/CoolingOffPeriodPanel'
import { AddSubjectToFinance } from './modals/AddSubjectToFinance'
import { AddBuildingAndPest } from './modals/AddBuildingAndPest'
import { AddBodyCorporateRecords } from './modals/AddBodyCorporateRecords'
import { ContractDatePanel } from './panels/ContractDatePanel'
import LoadingState from 'components/loadingState/LoadingState'
import { WaiverOfCoolingOffPanel } from './panels/WaiverOfCoolingOffPanel'
import { AddWaiverOfCoolingOff } from './modals/AddWaiverOfCoolingOff'
import { LinkedDateWarningModal } from './modals/LinkedDateWarningModal'
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import { isEqual } from 'lodash-es'
import useRexDisconnectedModal, {
  RexDisconnectedModalChange,
} from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal'
import { dateOnlyToDate, toDateOnlyString } from 'utils/date'

const mapImpactSourceDateType = (
  sourceDateType: LinkedDateImpactSourceDateType | undefined
) => {
  switch (sourceDateType) {
    case LinkedDateImpactSourceDateType.FinanceDate:
      return StaffPreviewLinkedDatesRequestSourceDateType.FinanceDate
    case LinkedDateImpactSourceDateType.BuildingAndPestDate:
      return StaffPreviewLinkedDatesRequestSourceDateType.BuildingAndPestDate
    case LinkedDateImpactSourceDateType.SpecialConditionDate:
      return StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate
    case LinkedDateImpactSourceDateType.ContractConditionChanges:
      return StaffPreviewLinkedDatesRequestSourceDateType.ContractConditionChanges
    case LinkedDateImpactSourceDateType.ContractDate:
    default:
      return StaffPreviewLinkedDatesRequestSourceDateType.ContractDate
  }
}

const getLinkedDateImpactSourceDateTypes = (impactedDates: LinkedDateImpact[]) =>
  [
    ...new Set(
      impactedDates.map((impact) =>
        mapImpactSourceDateType(impact.sourceDateType)
      )
    ),
  ]

const ContractDates = () => {
  const { campaign } = useSelectedCampaign()
  const { contract, isDocuSigned } = useSelectedContract()
  const [isLoaded, setIsLoaded] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const [linkedDateImpacts, setLinkedDateImpacts] = useState<
    LinkedDateImpact[]
  >([])
  const [linkedDateSourceTypes, setLinkedDateSourceTypes] = useState<
    StaffPreviewLinkedDatesRequestSourceDateType[]
  >([])
  const [pendingLinkedDateSaveSuccess, setPendingLinkedDateSaveSuccess] =
    useState<(() => void) | undefined>()
  const invalidateUseStaffGetContractDates =
    useInvalidateUseStaffGetContractDates()

  const {
    data: contractDates,
    isPending,
    isError,
  } = useStaffGetContractDates(
    campaign?.campaignId || '',
    contract?.contractId || '',
    !!campaign?.campaignId && !!contract?.contractId
  )

  const [contractDate, setContractDate] = useState<Date | undefined>(
    dateOnlyToDate(contractDates?.contractDate) ?? undefined
  )

  const [coolingOffDetails, setCoolingOffDetails] = useState<
    CoolingOffDetails | undefined
  >(contractDates?.coolingOffDetails)

  const [waiverOfCoolingOff, setWaiverOfCoolingOff] = useState<
    WaiverOfCoolingOff | undefined
  >(contractDates?.waiverOfCoolingOff)

  const [subjectToFinance, setSubjectToFinance] = useState<
    FormCondition | undefined
  >(contractDates?.subjectToFinance)

  const [buildingAndPestCondition, setBuildingAndPestCondition] = useState<
    FormCondition | undefined
  >(contractDates?.buildingAndPestCondition)

  const [bodyCorporateRecordsCondition, setBodyCorporateRecordsCondition] =
    useState<FormCondition | undefined>(
      contractDates?.bodyCorporateRecordsCondition
    )

  const { modal: rexDisconnectedModal, openWithChanges } =
    useRexDisconnectedModal()

  useEffect(() => {
    setContractDate(dateOnlyToDate(contractDates?.contractDate) ?? undefined)
    setCoolingOffDetails(contractDates?.coolingOffDetails)
    setWaiverOfCoolingOff(contractDates?.waiverOfCoolingOff)
    setSubjectToFinance(contractDates?.subjectToFinance)
    setBuildingAndPestCondition(contractDates?.buildingAndPestCondition)
    setBodyCorporateRecordsCondition(
      contractDates?.bodyCorporateRecordsCondition
    )
    setIsLoaded(!isPending)
  }, [contractDates, isPending])

  const { staffUpdateContractDates, staffUpdateContractDatesPending } =
    useStaffUpdateContractDates({
      onSuccess: () => {},
      onError: () => {},
    })

  const {
    staffPreviewLinkedDates,
    staffPreviewLinkedDatesPending,
  } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {},
  })

  const { data: rexListing, isPending: isPendingRexListing } =
    useGetListingByCampaignId(
      campaign?.campaignId || '',
      campaign?.campaignId !== undefined
    )

  const hasContractDateChanged = !isEqual(
    dateOnlyToDate(contractDates?.contractDate)?.toString(),
    contractDate?.toString()
  )

  const hasContractConditionDateInputsChanged =
    !isDirtyEqual(coolingOffDetails, contractDates?.coolingOffDetails) ||
    !isDirtyEqual(waiverOfCoolingOff, contractDates?.waiverOfCoolingOff) ||
    !isDirtyEqual(subjectToFinance, contractDates?.subjectToFinance) ||
    !isDirtyEqual(
      buildingAndPestCondition,
      contractDates?.buildingAndPestCondition
    ) ||
    !isDirtyEqual(
      bodyCorporateRecordsCondition,
      contractDates?.bodyCorporateRecordsCondition
    )

  const isFormDirty =
    hasContractDateChanged ||
    hasContractConditionDateInputsChanged

  const saveContractDates = async (success: () => void) => {
    if (!contract?.contractId) return

    if (!rexListing && isPendingRexListing === false) {
      const newContractDate = contractDate

      const changes: RexDisconnectedModalChange[] = []

      if (hasContractDateChanged) {
        changes.push({
          field: RexDisconnectedModalFields.ContractDate,
          value: newContractDate?.toLocaleDateString() || '',
        })
      }
      if (hasContractConditionDateInputsChanged) {
        changes.push({
          field: RexDisconnectedModalFields.ExpectedUnconditionalDate,
          value: '',
        })
      }

      openWithChanges(changes)
    }

    const request: StaffUpdateContractDatesRequest = {
      contractDate: toDateOnlyString(contractDate) as StaffUpdateContractDatesRequest['contractDate'],
      coolingOffDetails: coolingOffDetails,
      waiverOfCoolingOff: waiverOfCoolingOff,
      subjectToFinance: subjectToFinance,
      buildingAndPestCondition: buildingAndPestCondition,
      bodyCorporateRecordsCondition: bodyCorporateRecordsCondition,
    }

    await staffUpdateContractDates(
      {
        body: request,
        contractId: contract.contractId,
      },
      {
        onSuccess: async (data) => {
          if (data?.success) {
            success()
            await invalidateUseStaffGetContractDates(
              campaign?.campaignId ?? '',
              contract.contractId
            )
          } else {
            setSubmitFormErrors(data?.errors ?? [])
            setOpenErrorModal(true)
          }
        },
      }
    )
  }

  const shouldPreviewContractDateImpacts =
    campaign?.state === CampaignDetailModelState.QLD &&
    hasContractDateChanged

  const shouldPreviewContractConditionChangeImpacts =
    campaign?.state === CampaignDetailModelState.QLD &&
    hasContractConditionDateInputsChanged

  const onSave = async (success: () => void) => {
    if (!campaign?.campaignId || !contract?.contractId) return

    const campaignId = campaign.campaignId
    const contractId = contract.contractId

    const sourceDateType = shouldPreviewContractDateImpacts
      ? StaffPreviewLinkedDatesRequestSourceDateType.ContractDate
      : shouldPreviewContractConditionChangeImpacts
        ? StaffPreviewLinkedDatesRequestSourceDateType.ContractConditionChanges
        : undefined

    if (sourceDateType) {
      const request: StaffPreviewLinkedDatesRequest = {
        sourceDateType,
        ...(sourceDateType ===
          StaffPreviewLinkedDatesRequestSourceDateType.ContractDate && {
          proposedDate: toDateOnlyString(contractDate),
        }),
        coolingOffDetails,
        waiverOfCoolingOff,
        subjectToFinance,
        buildingAndPestCondition,
        bodyCorporateRecordsCondition,
      }

      const preview = await staffPreviewLinkedDates({
        campaignId,
        contractId,
        body: request,
      })
      const impactedDates = preview?.impactedDates ?? []

      if (impactedDates.length > 0) {
        setLinkedDateImpacts(impactedDates)
        setLinkedDateSourceTypes(
          getLinkedDateImpactSourceDateTypes(impactedDates)
        )
        setPendingLinkedDateSaveSuccess(() => success)
        return
      }
    }

    await saveContractDates(success)
  }

  const onCancelLinkedDateWarning = () => {
    setLinkedDateImpacts([])
    setLinkedDateSourceTypes([])
    setPendingLinkedDateSaveSuccess(undefined)
  }

  const onConfirmLinkedDateWarning = async () => {
    const success = pendingLinkedDateSaveSuccess
    setLinkedDateImpacts([])
    setLinkedDateSourceTypes([])
    setPendingLinkedDateSaveSuccess(undefined)

    if (success) {
      await saveContractDates(success)
    }
  }

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaign?.campaignId,
    contract?.contractId
  )

  if (!isLoaded) {
    return <LoadingState />
  }

  return (
    <ContractSubPage
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.CONDITIONS}
      subPages={getContractInformationSubPages()}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={
        staffUpdateContractDatesPending ||
        staffPreviewLinkedDatesPending
      }
      isError={isError}
    >
      <ContractDatePanel
        id="contractDate"
        key={`ContractDatePanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Contract Date"
        contractDate={contractDate}
        onChange={(contractDate) => setContractDate(contractDate)}
        isDocuSigned={isDocuSigned}
      />
      {coolingOffDetails &&
        campaign?.state === CampaignDetailModelState.NSW && (
          <CoolingOffPeriodPanel
            key={`CoolingOffPanel${isPending ? 'Loaded' : ''}-${contractDate}`}
            contractDate={contractDate}
            coolingOffDetails={coolingOffDetails}
            onChange={(updatedCoolingOffDetails) =>
              setCoolingOffDetails(updatedCoolingOffDetails)
            }
            onDelete={() => setCoolingOffDetails(undefined)}
            hideEditButton={
              !contract?.contractStage ||
              ![
                NswContractStage.Accepted,
                NswContractStage.Unconditional,
              ].includes(contract.contractStage as NswContractStage)
            }
            hideDeleteButton={
              !contract?.contractStage ||
              ![
                NswContractStage.Accepted,
                NswContractStage.Unconditional,
              ].includes(contract.contractStage as NswContractStage)
            }
          />
        )}
      {waiverOfCoolingOff &&
        campaign?.state === CampaignDetailModelState.QLD && (
          <WaiverOfCoolingOffPanel
            key={`CoolingOffPanel${isPending ? 'Loaded' : ''}-${contractDate}`}
            contractDate={contractDate}
            waiverOfCoolingOff={waiverOfCoolingOff}
            onChange={(updatedWaiverOfCoolingOff) =>
              setWaiverOfCoolingOff(updatedWaiverOfCoolingOff)
            }
            onDelete={() => setWaiverOfCoolingOff(undefined)}
            hideEditButton={
              !contract?.contractStage ||
              ![
                QldContractStage.Accepted,
                QldContractStage.Unconditional,
              ].includes(contract.contractStage as QldContractStage)
            }
            hideDeleteButton={
              !contract?.contractStage ||
              ![
                QldContractStage.Accepted,
                QldContractStage.Unconditional,
              ].includes(contract.contractStage as QldContractStage)
            }
          />
        )}

      {subjectToFinance?.condition &&
        subjectToFinance.condition !== Condition.NotSubjectToFinance && (
          <ConditionPanel
            id="subjectToFinance"
            key={`SubjectToFinancePanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Finance"
            contractDate={contractDate}
            condition={subjectToFinance}
            onChange={(condition) => {
              setSubjectToFinance(condition as FormCondition | undefined)
            }}
            onDelete={() => setSubjectToFinance(undefined)}
            specialConditionIdToClauseNumberMap={
              specialConditionIdToClauseNumberMap ?? {}
            }
          />
        )}

      {buildingAndPestCondition?.condition &&
        buildingAndPestCondition.condition !== Condition.NotApplicable && (
          <ConditionPanel
            id="buildingAndPestCondition"
            key={`BuildingAndPestConditionPanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Building & Pest"
            contractDate={contractDate}
            condition={buildingAndPestCondition}
            onChange={(condition) => {
              setBuildingAndPestCondition(
                condition as FormCondition | undefined
              )
            }}
            onDelete={() => setBuildingAndPestCondition(undefined)}
            onDateConditions={[Condition.OnSpecificDate]}
            fromDateConditions={[Condition.NdaysFromDate]}
            specialConditionIdToClauseNumberMap={
              specialConditionIdToClauseNumberMap ?? {}
            }
          />
        )}

      {bodyCorporateRecordsCondition?.condition &&
        bodyCorporateRecordsCondition.condition !== Condition.NotApplicable && (
          <ConditionPanel
            id="bodyCorporateRecordsCondition"
            key={`BodyCorporateRecordsConditionPanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Body Corporate Records"
            contractDate={contractDate}
            condition={bodyCorporateRecordsCondition}
            onChange={(condition) => {
              setBodyCorporateRecordsCondition(
                condition as FormCondition | undefined
              )
            }}
            onDelete={() => setBodyCorporateRecordsCondition(undefined)}
            onDateConditions={[Condition.OnSpecificDate]}
            fromDateConditions={[Condition.NdaysFromDate]}
            specialConditionIdToClauseNumberMap={
              specialConditionIdToClauseNumberMap ?? {}
            }
          />
        )}

      {!isPending && (
        <AgxRow gap="spacing-md" justifyContent="left" alignItems="flex-start">
          {!coolingOffDetails &&
            campaign?.state === CampaignDetailModelState.NSW && (
              <AddCoolingOffPeriod
                id="add-cooling-off"
                onConfirm={setCoolingOffDetails}
              />
            )}
          {!waiverOfCoolingOff &&
            campaign?.state === CampaignDetailModelState.QLD && (
              <AddWaiverOfCoolingOff
                id="add-cooling-off"
                onConfirm={setWaiverOfCoolingOff}
              />
            )}
          {(!subjectToFinance?.condition ||
            subjectToFinance.condition === Condition.NotSubjectToFinance) && (
            <AddSubjectToFinance
              id="add-finance"
              onConfirm={setSubjectToFinance}
            />
          )}
          {(!buildingAndPestCondition?.condition ||
            buildingAndPestCondition.condition === Condition.NotApplicable) && (
            <AddBuildingAndPest
              id="add-building-and-pest"
              onConfirm={setBuildingAndPestCondition}
            />
          )}
          {(!bodyCorporateRecordsCondition?.condition ||
            bodyCorporateRecordsCondition.condition ===
              Condition.NotApplicable) && (
            <AddBodyCorporateRecords
              id="add-body-corporate-records"
              onConfirm={setBodyCorporateRecordsCondition}
            />
          )}
        </AgxRow>
      )}

      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
      {linkedDateImpacts.length > 0 && linkedDateSourceTypes.length > 0 && (
        <LinkedDateWarningModal
          impactedDates={linkedDateImpacts}
          sourceDateTypes={linkedDateSourceTypes}
          onCancel={onCancelLinkedDateWarning}
          onConfirm={onConfirmLinkedDateWarning}
        />
      )}
      {rexDisconnectedModal}
    </ContractSubPage>
  )
}

export default ContractDates
